import React from 'react';
import { Icon } from '@iconify/react';
import { useGet, useSet, useLastResolved } from 'ccstate-react';
import { fileExplorerState } from '../state/fileExplorerState';
import { setSortOptions$, selectAllFiles$, clearSelection$ } from '../commands/fileCommands';
import FileListItem from './FileListItem';
import type { FileItem, SortOption, SortDirection } from '../../common/schema/schemaType';
interface FileListProps {
    onFileRightClick?: (file: FileItem, event: React.MouseEvent) => void;
}
export default function FileList({ onFileRightClick }: FileListProps) {
    const searchQuery = useGet(fileExplorerState.searchQuery$);
    const selectedFileIds = useGet(fileExplorerState.selectedFileIds$);
    const sortBy = useGet(fileExplorerState.sortBy$);
    const sortDirection = useGet(fileExplorerState.sortDirection$);
    const currentFiles = useLastResolved(fileExplorerState.currentFiles$) || [];
    const searchResults = useLastResolved(fileExplorerState.searchResults$);
    const setSortOptions = useSet(setSortOptions$);
    const selectAllFiles = useSet(selectAllFiles$);
    const clearSelection = useSet(clearSelection$);
    const displayFiles = (searchQuery as string).trim() ? ((searchResults as any)?.files || []) : currentFiles;
    const handleSort = (newSortBy: SortOption) => {
        const newDirection: SortDirection = sortBy === newSortBy && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortOptions(newSortBy, newDirection);
    };
    const handleSelectAll = () => {
        if ((selectedFileIds as string[]).length === displayFiles.length) {
            clearSelection();
        }
        else {
            selectAllFiles();
        }
    };
    const getSortIcon = (column: SortOption) => {
        if (sortBy !== column)
            return 'lucide:chevrons-up-down';
        return sortDirection === 'asc' ? 'lucide:chevron-up' : 'lucide:chevron-down';
    };
    if (displayFiles.length === 0) {
        return (<div className="flex-1 flex items-center justify-center" data-testid="empty-state" data-id="iw8yx6">
        <div className="text-center" data-id="gr4g8l">
          <div className="w-24 h-24 mx-auto mb-4 bg-[#F0F4F8] rounded-full flex items-center justify-center" data-id="072kof">
            <div className="w-12 h-12 text-[#B8C1CC]" data-id="2ql3oc">
              📁
            </div>
          </div>
          <h3 className="text-lg font-semibold text-[#1A2B3D] mb-2" data-id="hmd8pm">
            {(searchQuery as string).trim() ? 'No files found' : 'This folder is empty'}
          </h3>
          <p className="text-[#8B99A8] mb-6" data-id="kltlyw">
            {(searchQuery as string).trim()
                ? `No files match "${searchQuery}"`
                : 'Upload files or create folders to get started'}
          </p>
        </div>
      </div>);
    }
    return (<div className="flex-1 flex flex-col" data-testid="file-list" data-id="k564ll">
      {/* Header */}
      <div className="flex items-center p-3 border-b border-[#E0E7EF] bg-[#FAFBFD]" data-id="m3uihz">
        {/* Select all checkbox */}
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" data-id="kw6asa">
          <button onClick={handleSelectAll} className="w-4 h-4 border border-[#D1DAE5] rounded flex items-center justify-center bg-white hover:border-[#4A90E2]" data-testid="select-all-checkbox" data-id="61ngly">
            {(selectedFileIds as string[]).length === displayFiles.length && displayFiles.length > 0 && (<Icon icon="lucide:check" className="w-3 h-3 text-[#4A90E2]" data-id="vypj55"/>)}
          </button>
        </div>

        {/* Name column */}
        <div className="flex-1 min-w-0" data-id="9s8nzp">
          <button onClick={() => handleSort('name')} className="flex items-center gap-2 text-sm font-semibold text-[#1A2B3D] hover:text-[#4A90E2]" data-testid="sort-by-name" data-id="udpydq">
            Name
            <Icon icon={getSortIcon('name')} className="w-4 h-4" data-id="mmxh9p"/>
          </button>
        </div>

        {/* Size column */}
        <div className="w-20 flex-shrink-0" data-id="pluole">
          <button onClick={() => handleSort('size')} className="flex items-center gap-2 text-sm font-semibold text-[#1A2B3D] hover:text-[#4A90E2]" data-testid="sort-by-size" data-id="did5id">
            Size
            <Icon icon={getSortIcon('size')} className="w-4 h-4" data-id="wmemxc"/>
          </button>
        </div>

        {/* Date column */}
        <div className="w-24 flex-shrink-0" data-id="hoyx80">
          <button onClick={() => handleSort('date')} className="flex items-center gap-2 text-sm font-semibold text-[#1A2B3D] hover:text-[#4A90E2]" data-testid="sort-by-date" data-id="t2xl3s">
            Modified
            <Icon icon={getSortIcon('date')} className="w-4 h-4" data-id="kdseld"/>
          </button>
        </div>

        {/* Actions column */}
        <div className="w-6 flex-shrink-0" data-id="mbpipd"/>
      </div>

      {/* File list */}
      <div className="flex-1 overflow-auto" data-id="jed9xw">
        {displayFiles.map((file: any) => (<FileListItem key={file.id} file={file} isSelected={(selectedFileIds as string[]).includes(file.id)} onRightClick={(e) => onFileRightClick?.(file, e)} data-id="ryhju7"/>))}
      </div>
    </div>);
}
